import { PostHog, posthog } from "posthog-js";
import React, { useEffect } from "react";
import { browserHistory } from "react-router";
import { useSessionStore } from "../storez/SessionStore";
import { useDevicePermissionsStore } from "../storez/DevicePermissionsStore";
import { parseEnvVarToStringArray } from "./envUtils";
import { FilteringRule, YoutubeListCriteriaType } from "../pages/filtering/types";
import { ObjectPool, YoutubeDisplayTypeIndex } from "../types/Objects";

declare global {
  interface Window {
    posthog: PostHog;
  }
}

const shouldLoadPostHog = (): boolean => {
  const enabledDomains = parseEnvVarToStringArray(process.env.REACT_APP_POSTHOG_ENABLED_ON_DOMAINS);
  if (enabledDomains.length === 0) return false;
  return enabledDomains.includes(window.location.hostname);
};

export const initPostHog = (): void => {
  if (!shouldLoadPostHog()) {
    return;
  }

  if (posthog.__loaded) {
    return;
  }

  const key = (process.env.REACT_APP_POSTHOG_KEY as string) || "";
  const apiHost = (process.env.REACT_APP_POSTHOG_API_HOST as string) || "https://app.posthog.com";
  const uiHost = (process.env.REACT_APP_POSTHOG_UI_HOST as string) || "https://app.posthog.com";

  // Need to assign to window.posthog to make toolbar available
  window.posthog = posthog.init(key, {
    api_host: apiHost,
    ui_host: uiHost,
    autocapture: true,
    capture_pageview: false,
    capture_performance: true,
    disable_session_recording: false,
    session_recording: {
      recordCrossOriginIframes: true,
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },
  });
};

export const identifyPostHog = (): void => {
  if (!shouldLoadPostHog()) {
    return;
  }

  if (!posthog.__loaded) {
    return;
  }

  // Use try-catch to handle errors of no user or no device
  try {
    const { username } = useSessionStore.getState().getUser();
    const { id: applianceId, name: applianceName } = useSessionStore.getState().getDevice();
    const distinctId = `${username} + ${applianceId}`;
    const permissions = useDevicePermissionsStore.getState().permissions;

    posthog.identify(distinctId, {
      username,
      email: username,
      applianceId,
      applianceName,
      appliancePermissions: permissions,
    });
  } catch (e) {
    // If we can't identify the user, we don't want to crash the app
    return;
  }
};

export const resetPostHog = (): void => {
  if (!shouldLoadPostHog()) {
    return;
  }

  if (!posthog.__loaded) {
    return;
  }

  posthog.reset();
};

export const usePageViews = (): void => {
  useEffect(() => {
    if (!shouldLoadPostHog()) {
      return;
    }

    const history = browserHistory;
    let lastPath = "";

    const capturePageView = (): void => {
      const currentPath = window.location.pathname;

      if (currentPath !== lastPath && posthog.__loaded) {
        posthog.capture("$pageview", {
          $current_url: window.location.href,
          $pathname: currentPath,
        });
        lastPath = currentPath;
      }
    };

    // Capture initial pageview
    capturePageView();

    // Listen for route changes
    const unlisten = history.listen(() => {
      setTimeout(capturePageView, 0);
    });

    return () => {
      if (typeof unlisten === "function") {
        unlisten();
      }
    };
  }, []);
};

export const PageViewTracker: React.FC = () => {
  usePageViews();
  return null;
};

export const CaptureYoutubeObjectListsUsed = ({ criteria, id }: FilteringRule): void => {
  if (!criteria.some(({ type }) => type === YoutubeListCriteriaType)) return;

  const listIds = criteria
    .filter(({ type }) => type === YoutubeListCriteriaType)
    .map(({ conditions }) => conditions)
    .flat();

  if (listIds.length === 0) return;

  const metadata = {
    list_ids: listIds,
    rule_id: id,
  };

  posthog.capture("youtube_object_lists_used", metadata);
};

export const CaptureYoutubeObjectListCreated = ({ id, type }: ObjectPool): void => {
  if (type !== YoutubeDisplayTypeIndex || !id) return;

  const metadata = {
    list_id: id,
  };

  posthog.capture("youtube_object_list_created", metadata);
};

export const CaptureYoutubeObjectListUpdated = ({ id, type }: ObjectPool): void => {
  if (type !== YoutubeDisplayTypeIndex || !id) return;

  const metadata = {
    list_id: id,
  };

  posthog.capture("youtube_object_list_updated", metadata);
};
